import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Star } from "lucide-react";

/** Public plan showcase used on Login "Our Plans" tab and /pricing route.
 *  Click on any plan → /signup?plan_id=<id>. */
export default function PlanShowcase({ compact = false }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/payments/plans/public")
      .then(({ data }) => setPlans(data.items || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  // Canonical order: Free Trial → Monthly → Quarterly → Half-Yearly → Yearly.
  const rank = { monthly: 1, quarterly: 2, half_yearly: 3, yearly: 4 };
  const sorted = [...plans].sort((a, b) => {
    const at = a.is_trial ? 0 : (rank[a.duration] || 9);
    const bt = b.is_trial ? 0 : (rank[b.duration] || 9);
    if (at !== bt) return at - bt;
    return (a.sort_order || 100) - (b.sort_order || 100);
  });

  if (loading) return <div className="py-8 text-center text-sm text-ink-muted">Loading plans…</div>;
  if (!sorted.length) return <div className="py-8 text-center text-sm text-ink-muted">No plans available yet. Please check back soon.</div>;

  return (
    <div className={`grid gap-4 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"}`} data-testid="plan-showcase">
      {sorted.map((p) => {
        const isTrial = p.is_trial && p.trial_days > 0;
        const hasMrp = p.mrp_inr > 0 && p.mrp_inr > p.price_inr;
        const disc = hasMrp ? Math.round(((p.mrp_inr - p.price_inr) / p.mrp_inr) * 100) : 0;
        return (
          <Card
            key={p.id}
            className={`relative flex flex-col p-4 transition hover:-translate-y-0.5 hover:shadow-lg
              ${p.is_recommended ? "border-amber-400 ring-2 ring-amber-300/40" : ""}
              ${isTrial ? "border-emerald-400 bg-gradient-to-b from-emerald-50/60 to-white" : ""}`}
            data-testid={`showcase-plan-${p.code || p.id}`}
          >
            {p.is_recommended && (
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-md">
                <Star size={9} className="mr-0.5 inline" />Best for you
              </div>
            )}
            <div className="mb-1 flex items-center gap-1.5">
              {isTrial && <Sparkles size={13} className="text-emerald-600" />}
              <div className="font-heading text-base font-semibold text-brand">{p.name}</div>
            </div>
            {p.description && <div className="mb-2 line-clamp-2 text-[11px] text-ink-muted">{p.description}</div>}

            <div className="mb-1 flex items-baseline gap-1.5">
              {isTrial ? (
                <span className="font-heading text-2xl text-emerald-700">FREE</span>
              ) : (
                <span className="font-heading text-2xl">₹ {Number(p.price_inr).toLocaleString("en-IN")}</span>
              )}
              {hasMrp && (
                <span className="text-xs text-ink-muted line-through">₹{Number(p.mrp_inr).toLocaleString("en-IN")}</span>
              )}
            </div>
            {hasMrp && (
              <div className="mb-2 inline-block self-start rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                {disc}% off
              </div>
            )}
            <div className="mb-3 text-[11px] capitalize text-ink-muted">
              {isTrial ? `${p.trial_days} days free trial` : `per ${(p.duration || "year").replace("_", "-")} · ${p.validity_months} mo validity`}
            </div>

            <div className="mb-3 space-y-1 text-[11px] text-ink">
              <FeatureLine ok>{p.branch_limit} branch{p.branch_limit > 1 ? "es" : ""}</FeatureLine>
              <FeatureLine ok>{p.user_limit} users</FeatureLine>
              <FeatureLine ok>{p.storage_limit_gb} GB storage</FeatureLine>
              {(p.features || []).slice(0, 3).map((f, i) => <FeatureLine ok key={i}>{f}</FeatureLine>)}
            </div>

            <Link
              to={`/signup?plan_id=${p.id}`}
              className={`mt-auto block rounded-md px-3 py-2 text-center text-xs font-semibold text-white transition
                ${isTrial ? "bg-emerald-600 hover:bg-emerald-700" : "bg-brand hover:bg-brand-700"}`}
              data-testid={`showcase-cta-${p.code || p.id}`}
            >
              {isTrial ? "Start free trial →" : "Choose & sign up →"}
            </Link>
          </Card>
        );
      })}
    </div>
  );
}

function FeatureLine({ children, ok = true }) {
  return (
    <div className="flex items-center gap-1.5">
      <Check size={11} className={ok ? "text-emerald-600" : "text-ink-muted"} />
      <span>{children}</span>
    </div>
  );
}
